import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { ArrowRight, Check, ChevronDown, Target, Wand2, Bot, Zap, Radio, BarChart3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const CALENDLY_URL = 'https://calendly.com/contact-bankkimedia/30min';
const iconMap = { 'brand-authority': Target, 'lead-capture': Wand2, 'crm-automation': Bot, 'ai-sales-desk': Zap, 'distribution': Radio, 'roi-dashboards': BarChart3 };

const FAQ = ({ faqs }) => {
  const [open, setOpen] = useState(null);
  return (
    <div className="space-y-2">
      {faqs.map((faq, idx) => (
        <div key={idx} className="border border-white/10 rounded-xl overflow-hidden bg-white/[0.02]">
          <button onClick={() => setOpen(open === idx ? null : idx)} className="w-full flex items-center justify-between p-4 text-left">
            <span className="text-white text-sm font-medium pr-4">{faq.q}</span>
            <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${open === idx ? 'rotate-180' : ''}`} />
          </button>
          <AnimatePresence>
            {open === idx && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                <p className="px-4 pb-4 text-gray-400 text-sm leading-relaxed">{faq.a}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

const ServicesPage = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const hero = t('services.hero', { returnObjects: true });
  const bottom = t('services.bottomCta', { returnObjects: true });
  const services = t('services.items', { returnObjects: true });

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <SEO title="Services" />
      <Header />
      <main>
        <section className="pt-28 pb-12 md:pt-36 md:pb-16">
          <div className="container-custom text-center max-w-3xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <h1 className="text-4xl md:text-6xl font-bold font-['Syne'] leading-tight mb-4">{hero.title1}<br /><span className="text-[#c8ff00]">{hero.title2}</span></h1>
              <p className="text-white/50 text-lg max-w-xl mx-auto">{hero.desc}</p>
            </motion.div>
          </div>
        </section>

        <section className="sticky top-20 z-30 bg-[#0a0a0a]/95 backdrop-blur-md border-y border-white/5">
          <div className="container-custom">
            <div className="flex overflow-x-auto gap-1 py-3 scrollbar-hide">
              {services.map((s) => {
                const Icon = iconMap[s.id];
                return (
                  <a key={s.id} href={`#${s.id}`} className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-400 hover:text-white whitespace-nowrap rounded-full hover:bg-white/5 transition-all">
                    {Icon && <Icon className="w-3.5 h-3.5" />}
                    {s.label}
                  </a>
                );
              })}
            </div>
          </div>
        </section>

        {services.map((service) => {
          const Icon = iconMap[service.id];
          return (
            <section key={service.id} id={service.id} className="py-16 md:py-24 border-b border-white/5 scroll-mt-32">
              <div className="container-custom max-w-4xl">
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-10">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-[#c8ff00]/10 flex items-center justify-center">{Icon && <Icon className="w-4 h-4 text-[#c8ff00]" />}</div>
                    <span className="text-[#c8ff00] text-xs font-bold uppercase tracking-[0.15em]">{service.label}</span>
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white mb-3">{service.headline}</h2>
                  <p className="text-gray-400 text-base md:text-lg max-w-2xl">{service.oneLiner}</p>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-10">
                  <p className="text-gray-500 text-xs uppercase tracking-[0.2em] font-bold mb-4">{t('services.whatYouGet')}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {service.deliverables.map((d) => <div key={d} className="flex items-center gap-2 py-2 text-gray-300 text-sm"><Check className="w-4 h-4 text-[#c8ff00] flex-shrink-0" />{d}</div>)}
                  </div>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-10">
                  <p className="text-gray-500 text-xs uppercase tracking-[0.2em] font-bold mb-4">{t('services.pricing')}</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {service.pricing.map((tier, idx) => (
                      <div key={tier.tier} className={`p-5 rounded-xl border transition-all ${idx === 1 ? 'border-[#c8ff00]/30 bg-[#c8ff00]/[0.03]' : 'border-white/10 bg-white/[0.02]'}`}>
                        <p className="text-white text-sm font-bold mb-1">{tier.tier}</p>
                        <p className="text-[#c8ff00] text-2xl font-bold font-['Syne'] mb-1">{tier.price}</p>
                        <p className="text-gray-500 text-xs">{tier.note}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-gray-600 text-xs mt-3">{t('services.pricingNote')}</p>
                </motion.div>

                <FAQ faqs={service.faqs} />

                <div className="mt-8">
                  <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-[#c8ff00] text-black font-bold rounded-full text-sm hover:bg-[#d4ff33] transition-colors">
                    {t('services.getStartedWith')} {service.label}
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </section>
          );
        })}

        <section className="py-16 md:py-24">
          <div className="container-custom max-w-3xl text-center">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-5xl font-bold font-['Syne'] text-white mb-4">{bottom.title}</h2>
              <p className="text-gray-400 text-lg mb-8">{bottom.desc}</p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-10 py-4 bg-[#c8ff00] text-black font-bold rounded-full text-lg hover:bg-[#d4ff33] transition-colors">
                  {bottom.cta}<ArrowRight className="w-5 h-5" />
                </a>
                <a href="/packages" className="inline-flex items-center gap-2 px-10 py-4 border border-white/20 text-white font-bold rounded-full text-lg hover:bg-white/5 transition-colors">
                  {bottom.packages}
                </a>
              </div>
              <p className="text-white/30 text-sm mt-4">{bottom.sub}</p>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ServicesPage;
